import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { FilterableList } from '../components/layouts';
import Button from '../components/ui/Button';
import Card, { CardContent } from '../components/ui/Card';
import { civicAPI } from '../domains/elections/api/civic';
import { usePageTitle } from '../hooks/shared';
import { stateName } from '../lib/states';

function getElectionType(name) {
  const lower = name.toLowerCase();
  if (lower.includes('primary')) return 'Primary';
  if (lower.includes('special')) return 'Special';
  if (lower.includes('runoff')) return 'Runoff';
  return 'General';
}

function getJurisdictionLabel(type) {
  const labels = {
    state: 'State',
    county: 'County',
    municipality: 'Local',
    school_district: 'School District',
    special_district: 'Special District',
  };
  return labels[type] || type;
}

function getSpecificJurisdictionName(election) {
  const { state, county, jurisdictionType } = election;
  if (jurisdictionType === 'state' && state) {
    return state;
  }
  if (jurisdictionType === 'county' && county) {
    return county;
  }
  if (jurisdictionType === 'municipality' && county) {
    return county;
  }
  if (jurisdictionType === 'school_district' && county) {
    return county;
  }
  return '';
}

// Group elections by: electionDay + electionType + jurisdictionType
function groupElections(elections) {
  const groups = new Map();

  elections.forEach((election) => {
    const key = `${election.electionDay}|${election.electionType}|${election.jurisdictionType}`;

    if (!groups.has(key)) {
      groups.set(key, {
        electionDay: election.electionDay,
        electionType: election.electionType,
        jurisdictionType: election.jurisdictionType,
        elections: [],
        name: election.name,
      });
    }

    groups.get(key).elections.push(election);
  });

  // Sort each group's elections
  groups.forEach((group) => {
    group.elections.sort((a, b) => {
      const aIter = a.state && !a.county ? 0 : a.county ? 1 : 2;
      const bIter = b.state && !b.county ? 0 : b.county ? 1 : 2;
      if (aIter !== bIter) return aIter - bIter;
      return (a.state || '').localeCompare(b.state || '');
    });
  });

  // Convert to array and sort by date
  return Array.from(groups.values()).sort((a, b) => {
    const dateA = new Date(`${a.electionDay}T00:00:00`);
    const dateB = new Date(`${b.electionDay}T00:00:00`);
    return dateA - dateB;
  });
}

const TYPE_STYLES = {
  General: 'bg-primary-100 text-primary-700',
  Primary: 'bg-purple-100 text-purple-700',
  Special: 'bg-amber-100 text-amber-700',
  Runoff: 'bg-orange-100 text-orange-700',
};

// ── SingleElectionItem ────────────────────────────────────────────────────────
// Shows one election within a grouped card
function SingleElectionItem({ election, onClick }) {
  return (
    <div
      className="px-3 py-2.5 border-b last:border-b-0 border-neutral-200 cursor-pointer hover:bg-neutral-50 transition-colors"
      onClick={() => onClick(election.id)}
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="font-medium text-sm text-neutral-900 flex-1">{election.name}</h4>
        {election.state && (
          <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-700 shrink-0">
            {election.state}
            {election.county && ` - ${election.county}`}
          </span>
        )}
      </div>
      {election.officialSource?.domain && (
        <div className="text-xs text-emerald-600 mt-1">{election.officialSource.domain}</div>
      )}
    </div>
  );
}

// ── GroupedElectionCard ────────────────────────────────────────────────────────
// Displays a grouped set of elections (same day, type, jurisdiction level)
function GroupedElectionCard({ group }) {
  const navigate = useNavigate();
  const [selectedElectionId, setSelectedElectionId] = useState(null);

  const type = getElectionType(group.elections[0].name);
  const date = new Date(`${group.electionDay}T00:00:00`);
  const isUpcoming = date >= new Date();
  const count = group.elections.length;
  const jurisdictionLabel = getJurisdictionLabel(group.jurisdictionType);
  const singleJurisdictionName = getSpecificJurisdictionName(group.elections[0]);

  const selectedElection =
    group.elections.find((e) => e.id === selectedElectionId) || group.elections[0];

  function handleCardClick() {
    navigate(`/elections/${selectedElection.id}`);
  }

  return (
    <Card patrioticShadow hover className="flex flex-col cursor-pointer" onClick={handleCardClick}>
      <CardContent className="p-5 flex flex-col flex-1">
        {/* Type + Jurisdiction + Count/Name */}
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <span
            className={`inline-block px-2.5 py-0.5 text-xs font-semibold rounded-full ${TYPE_STYLES[type]}`}
          >
            {type}
          </span>
          <span className="inline-block px-2.5 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-700">
            {jurisdictionLabel}
          </span>
          <span className="inline-block px-2.5 py-0.5 text-xs font-medium rounded-full bg-neutral-100 text-neutral-600">
            {count === 1 ? singleJurisdictionName : `${count} jurisdictions`}
          </span>
        </div>

        {/* Title */}
        <h3 className="font-semibold text-neutral-900 text-base leading-snug mb-2 flex-1">
          {group.elections[0].name}
        </h3>

        {/* Date Range: Early Voting Start to Election Day */}
        <div className="flex flex-col gap-2 mb-4">
          <div className="flex items-center gap-1.5 text-sm text-neutral-500">
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>
              {group.elections[0].earlyVotingStart ? (
                <>
                  {new Date(`${group.elections[0].earlyVotingStart}T00:00:00`).toLocaleDateString(
                    'en-US',
                    {
                      month: 'short',
                      day: 'numeric',
                    }
                  )}
                  –
                  {date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })}
                </>
              ) : (
                date.toLocaleDateString('en-US', {
                  weekday: 'short',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })
              )}
            </span>
            {isUpcoming && (
              <span className="ml-auto text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                Upcoming
              </span>
            )}
          </div>
          {group.elections[0].earlyVotingHours &&
            group.elections[0].earlyVotingHours.length > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/elections/${selectedElection.id}#voting`);
                }}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium underline underline-offset-2 text-left"
              >
                View voting times
              </button>
            )}
        </div>

        {/* Elections list for multi-jurisdiction groups */}
        {count > 1 && (
          <div className="border border-neutral-200 rounded-lg mb-4 overflow-hidden bg-neutral-50">
            {group.elections.map((election) => (
              <SingleElectionItem
                key={election.id}
                election={election}
                onClick={(id) => {
                  setSelectedElectionId(id);
                }}
              />
            ))}
          </div>
        )}

        <Button
          variant="outline"
          size="sm"
          className="w-full mt-auto"
          onClick={(e) => {
            e.stopPropagation();
            handleCardClick();
          }}
        >
          View Details
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Elections page ─────────────────────────────────────────────────────────────
export default function Elections() {
  const [searchParams] = useSearchParams();
  const search = searchParams.get('q') ?? '';
  const typeFilter = searchParams.get('type') ?? 'All';
  const jurisdictionFilter = searchParams.get('jurisdiction') ?? 'All';
  const location = useLocation();
  const locationStateCode = location.state?.stateCode ?? null;
  const [showAllStates, setShowAllStates] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['elections'],
    queryFn: () => civicAPI.getElections(),
  });

  const elections = data?.elections || [];

  const availableJurisdictions = useMemo(() => {
    const jurisdictions = new Set(elections.map((e) => e.jurisdictionType).filter(Boolean));
    return ['All', ...[...jurisdictions].sort()];
  }, [elections]);

  // Build a descriptive browser-history title from whatever filters are active
  const pageTitle = useMemo(() => {
    const parts = [];
    if (search) parts.push(`"${search}"`);
    if (jurisdictionFilter !== 'All') parts.push(getJurisdictionLabel(jurisdictionFilter));
    if (typeFilter !== 'All') parts.push(typeFilter);
    parts.push('Elections');
    return parts.join(' ');
  }, [search, typeFilter, jurisdictionFilter]);

  usePageTitle(pageTitle);

  // Filter function for FilterableList
  const filterFn = (group, filters) => {
    const matchesSearch =
      !filters.q ||
      group.elections.some((e) => e.name.toLowerCase().includes(filters.q.toLowerCase()));
    const matchesType =
      !filters.type ||
      filters.type === 'All' ||
      getElectionType(group.elections[0].name) === filters.type;
    const matchesJurisdiction =
      !filters.jurisdiction ||
      filters.jurisdiction === 'All' ||
      group.jurisdictionType === filters.jurisdiction;
    return matchesSearch && matchesType && matchesJurisdiction;
  };

  // Group elections and filter
  const groupedElections = useMemo(() => {
    return groupElections(elections);
  }, [elections]);

  const visibleGroups = useMemo(() => {
    if (!locationStateCode || showAllStates) return groupedElections;
    return groupedElections.filter((g) => g.elections.some((e) => e.state === locationStateCode));
  }, [groupedElections, locationStateCode, showAllStates]);

  const locationStateName = locationStateCode ? stateName(locationStateCode) : null;

  return (
    <>
      {locationStateCode && !showAllStates && (
        <div style={{ borderBottom: '2px solid #1a1a1a', background: '#ede5d4' }}>
          <div className="container-custom py-3 flex items-center justify-between gap-4">
            <span
              style={{
                fontFamily: "'Special Elite', monospace",
                fontSize: '13px',
                color: '#3d3528',
              }}
            >
              📍 Showing elections for {locationStateName || locationStateCode}
            </span>
            <button
              onClick={() => setShowAllStates(true)}
              style={{
                fontFamily: "'Patrick Hand', cursive",
                fontSize: '13px',
                color: '#d62828',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              Show all states ×
            </button>
          </div>
        </div>
      )}
      <FilterableList
        items={visibleGroups}
        filters={[
          {
            key: 'q',
            label: 'Search elections…',
            type: 'search',
            searchFn: (group, v) =>
              group.elections.some((e) => e.name.toLowerCase().includes(v.toLowerCase())),
            defaultValue: '',
          },
          {
            key: 'type',
            label: 'Type',
            type: 'select',
            options: [
              { value: 'All', label: 'All Types' },
              { value: 'General', label: 'General' },
              { value: 'Primary', label: 'Primary' },
              { value: 'Special', label: 'Special' },
              { value: 'Runoff', label: 'Runoff' },
            ],
            defaultValue: 'All',
          },
          {
            key: 'jurisdiction',
            label: 'Level',
            type: 'select',
            options: availableJurisdictions.map((j) => ({
              value: j,
              label: j === 'All' ? 'All Levels' : getJurisdictionLabel(j),
            })),
            defaultValue: 'All',
          },
        ]}
        filterFn={filterFn}
        renderItem={(group) => <GroupedElectionCard group={group} />}
        title={
          locationStateCode && !showAllStates
            ? `Elections in ${locationStateName || locationStateCode}`
            : 'Upcoming Elections'
        }
        subtitle={
          locationStateCode && !showAllStates
            ? `Filtered to elections in your state`
            : 'Browse and filter elections across the United States'
        }
        isLoading={isLoading}
        error={error}
      />
    </>
  );
}
